import { createPasskey, type PortalMe, type TotpEnrollBeginResponse } from '@central/api';
import { TotpQr, color, shownError, type Guard } from '@central/ui';
import { Alert, Button, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { errorMessage } from '../format';

interface SetupProps {
  onDone: (me: PortalMe) => void;
  /** Sign-in enrollment: trust this browser once the session is promoted. */
  trustBrowser?: boolean;
  /** Team & security: re-authenticates when the user already has a factor. */
  guard?: Guard;
}

const direct: Guard = (op) => op();

function ErrorAlert({ error }: { error: unknown }) {
  const e = shownError(error);
  return e ? <Alert color="red">{errorMessage(e)}</Alert> : null;
}

/**
 * Authenticator-app enrollment: begin shows the QR code and secret once,
 * finish proves the user can generate codes from it.
 */
export function TotpSetup({ onDone, trustBrowser, guard = direct }: SetupProps) {
  const [secret, setSecret] = useState<TotpEnrollBeginResponse>();
  const [code, setCode] = useState('');
  const begin = useMutation({
    mutationFn: () => guard(() => api.auth.totpEnrollBegin()),
    onSuccess: setSecret,
  });
  const finish = useMutation({
    mutationFn: () =>
      guard(() =>
        api.auth.totpEnrollFinish({
          code: code.replace(/\s/g, ''),
          ...(trustBrowser === undefined ? {} : { trustBrowser }),
        }),
      ),
    onSuccess: onDone,
  });
  const error = begin.error ?? finish.error;

  if (!secret) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button variant="default" loading={begin.isPending} onClick={() => begin.mutate()}>
          Set up an authenticator app
        </Button>
        <ErrorAlert error={error} />
      </div>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        finish.mutate();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <p style={{ margin: 0, fontSize: 14, color: color.textBody }}>
        Scan this code with your authenticator app, or enter the key by hand. It is shown only once.
      </p>
      <div style={{ alignSelf: 'center' }}>
        <TotpQr value={secret.otpauthUrl} />
      </div>
      <code
        data-testid="totp-secret"
        style={{
          background: color.page,
          borderRadius: 6,
          padding: '8px 10px',
          wordBreak: 'break-all',
        }}
      >
        {secret.secret}
      </code>
      <TextInput
        label="6-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.currentTarget.value)}
        required
      />
      <Button type="submit" loading={finish.isPending}>
        Turn on
      </Button>
      <ErrorAlert error={error} />
    </form>
  );
}

/** Registers a passkey on this device under a name the user picks. */
export function PasskeySetup({ onDone, trustBrowser, guard = direct }: SetupProps) {
  const [name, setName] = useState('');
  const register = useMutation({
    mutationFn: () =>
      guard(async () => {
        const { publicKey } = await api.auth.webauthnRegisterBegin();
        const credential = await createPasskey(publicKey);
        return api.auth.webauthnRegisterFinish({
          credential,
          name: name.trim() || 'Passkey',
          ...(trustBrowser === undefined ? {} : { trustBrowser }),
        });
      }),
    onSuccess: onDone,
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        register.mutate();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <TextInput
        label="Passkey name"
        placeholder="e.g. Work laptop"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <Button type="submit" variant="default" loading={register.isPending}>
        Add a passkey
      </Button>
      <ErrorAlert error={register.error} />
    </form>
  );
}
